<template>
    <ModalBase :show-modal="showModal" @close-modal="close" class="cost-modal">
        <template v-slot:titlebar>
            <TitleBar variant-class="title-modal">
                <template v-slot:pretitle>
                    {{ (isEdit) ? $gettext('EDIT'): $gettext('ADD') }}
                </template>
                <template v-slot:title>
                    {{ $gettext('Cost') }} {{ costNumber }}
                </template>
                <template v-slot:buttons>
                    <div v-if="isEdit" class="modal-action-bar">
                        <GekkoButton 
                            :text="$gettext('Delete')" 
                            icon="fa-solid fa-trash-can" 
                            class="text-danger"
                            v-gk-confirm="{
                                title: $gettext('Delete cost?'),
                                message: $gettext('Are you sure you want to delete this cost?'),
                                options: {
                                    variant: 'danger',
                                    confirmText: $gettext('Delete')
                                },
                                callback: deleteCost
                            }"
                        />
                        <GekkoButton 
                            @click="copyCost" 
                            :text="$gettext('Copy')" 
                            icon="fa-regular fa-copy" 
                            class="text-info"
                        />
                    </div>
                </template>
            </TitleBar>
        </template>
        <template v-slot:maincontent>
            <EditCost @created="costCreated" @edited="costEdited" @cost-edit-nav="costEditNav" :prev-next="prevNext"/>
        </template>
        <template v-slot:premiumtip>
            <GekkoButton 
                icon="fa-solid fa-circle-question"
                variant="link"
                size="lg"
                class="close-btn text-unassigned"
                style="right:1rem;"
                id="tt-premium-tip"
            />
            <b-tooltip :custom-class="'tooltip-padded'" target="tt-premium-tip" placement="bottom" triggers="hover">
                <div class="tooltip-title">
                    {{ $gettext('Premium Tip') }}
                </div>
                <div class="tooltip-text">
                    {{ premiumTipText }}
                </div>
            </b-tooltip>
        </template>
    </ModalBase>
</template>
<script>
    import { mapGetters } from "vuex";
    import TitleBar from "@/components/TitleBar";
    import EditCost from "@/components/costs/EditCost";
    import CostService from "@/store/services/cost.service.js"
    import GekkoButton from "@/components/GekkoButton";
    import ModalBase from "@/components/base_components/ModalBase";
    export default {
        name: "CostCreateModal",
        props: {
            showModalOn: {
                type: Array
            },
            initialCostList: {
                type: Array
            },
            initialNext: {
                type: String,
                default: null
            }
        },
        components: { ModalBase, GekkoButton, TitleBar, EditCost },
        watch: {
            $route: {
                immediate: true,
                handler(to) {
                    this.showModal = this.showModalOn.includes(to.name);
                    (this.showModal) ? document.body.classList.add('modal-on') : document.body.classList.remove('modal-on');
                    if (this.showModal) {
                        this.targetId = this.$route.params['costId']
                        this.checkPrevNext()
                    }
                }
            },
            initialCostList () {
                if (this.showModal) this.checkPrevNext()
            }
        },
        data() {
            return {
                showModal: false,
                isCopying: false,
                prevNext: { prev: false, next: false },
                currentNext: false,
                currentCostList: [],
                useCurrentNext: false,
                targetId: null,
                premiumTipText: this.$gettext("You can also add costs through email by forwarding your receipt from your account email to upload@costs.getgekko.com"),
            }
        },
        created() {
            window.addEventListener('keydown', this.closeEventListener);
        },
        destroyed() {
            window.removeEventListener('keydown', this.closeEventListener);
        },
        methods: {
            ...mapGetters({ getTempCostList: 'costStore/getTempCostList' }),
            async checkPrevNext () {
                // calculate cost position in store tempCostList to get prev & next
                // first time set it to initial cost list
                let cost_list = this.getTempCostList()

                if (cost_list.length > 0 && this.showModal) {
                    let prev = false, next = false
                    const costPos = cost_list.findIndex(item => item.id == this.targetId)
                    if (costPos > -1) { // this.targetId is in currentCostList
                        /**
                         * 'next' is most recent cost, so previous element on that currentCostList,
                         * should be already there unless it's first item (most recent)
                         */
                        next = (costPos > 0) ? (costPos - 1) : false
                        this.prevNext.next = (next || next === 0) ? cost_list[next].id : false
                        /**
                         * 'prev' is next item on cost list, could not be there yet, so check for next page.
                         *  if not last item on currentList, take next position as 'prev'
                         */
                        prev = (costPos + 1)
                        if (costPos < (cost_list.length - 1)) {
                            this.prevNext.prev = (prev || prev === 0) ? cost_list[prev].id : false
                        } else {
                            // if last item, check for next pages (if next page is avalable)
                            await this.getMoreCosts()
                            cost_list = this.getTempCostList()
                            this.prevNext.prev = (prev || prev === 0) ? cost_list[prev]?.id : false
                        }
                    } else { // this.targetId not in currentCostList
                        this.getMoreCosts(true)
                    }
                } 
            },
            async getMoreCosts (recurring = false) {
                if ((this.initialNext && !this.useCurrentNext) || (this.currentNext && this.useCurrentNext)) {
                    if(!this.currentNext) this.currentNext = this.initialNext
                    return CostService.loadNextPage(this.currentNext).then( (response) => {
                        if(response.data.results) {
                            this.useCurrentNext = true
                            this.currentNext = response.data.next
                            const cost_list = this.$store.getters['costStore/getTempCostList']
                            this.$store.commit('costStore/setTempCostList', cost_list.concat(response.data.results))
                            if(recurring) this.checkPrevNext()
                        }
                    })
                }
            },
            closeEventListener(event) {
                if (event.key === 'Escape') {
                    this.$router.push({name: 'Costs'});
                }
            },
            close() {
                this.$router.push({name: 'Costs'});
                this.$emit('cancel');
            },
            costCreated(value) {
                this.$emit('created', value);
                this.$router.push({name: 'Costs'});
            },
            costEdited(value) {
                this.$emit('edited', value);
                this.$router.push({name: 'Costs'});
            },
            costEditNav(value) {
                this.$emit('edited', value);
            },
            copyCost() {
                this.isCopying = true;
                if (!this.$store.getters['getIsPremium']) {
                    this.$store.commit('togglePremiumModal');
                    return;
                }
                let vm = this;
                let cost = this.$store.getters['costStore/getCostToEdit'];
                let alert_text = cost.number + " has been copied";
                delete cost.id;
                delete cost.number;
                var cost2 = CostService.prepareCostForUpload(cost);
                delete cost2.image;
                cost2.date = new Date().format("Y-m-d");
                delete cost.image;  // for now just remove the image forcefully
                CostService.create(cost2).then(function (response) {
                    vm.isCopying = false;
                    vm.$store.commit('setAlert', {title: vm.$gettext("Cost Created"), text:alert_text, variant:"success",duration:4});
                    vm.$emit('created', response.data);
                    vm.$router.push({name: 'EditCost', params: {costId: response.data.id}, force: true});
                });
            },
            deleteCost() {
                let vm = this;
                let cost = this.$store.getters['costStore/getCostToEdit'];
                CostService.destroy(cost.id).then(function () {
                    vm.$store.commit('setAlert', {title: vm.$gettext("Cost Deleted"), text:cost.number + " " + vm.$gettext("has been deleted"), variant:"success",duration:4});
                    vm.$router.push({name: 'Costs'});
                    vm.$emit('delete', cost.id);
                });
            },
        },
        computed: {
            costNumber() {
                return this.$store.getters['costStore/getCostNumber'];
            },
            cost() {
                return this.$store.getters['costStore/getCostToEdit'];
            },
            isEdit() {
                return this.$route.name === 'EditCost';
            },
        }
    };
</script>
<style lang="scss" scoped>
    .modal-on {
        overflow: hidden;
    }
</style>
<style lang="scss" scoped>

</style>
